import { useMemo } from 'react'

import { useProvincesQuery } from '@/hooks/business/useProvincesQuery'

interface UseProvincesListParams {
  countryId: number
}

interface ProvinceOption {
  value: string
  label: string
}

interface UseProvincesListReturn {
  provinceOptions: ProvinceOption[]
  isLoadingProvinces: boolean
  isErrorProvinces: boolean
  errorProvinces: Error | null
}

export function useProvincesList({
  countryId,
}: UseProvincesListParams): UseProvincesListReturn {
  // Validate countryId to prevent unnecessary API calls
  const isValidCountryId = Boolean(countryId && countryId > 0)

  const { provinces, isLoadingProvinces, isErrorProvinces, errorProvinces } =
    useProvincesQuery({
      params: { countryId },
      options: {
        enabled: isValidCountryId,
        staleTime: 10 * 60 * 1000, // 10 minutes cache
      },
    })

  // Transform provinces for select options with proper validation
  const provinceOptions = useMemo(() => {
    if (!provinces || !Array.isArray(provinces)) {
      return []
    }

    return provinces
      .filter((province) => province && province.id && province.name)
      .map((province) => ({
        value: province.id.toString(),
        label: province.name,
      }))
  }, [provinces])

  return {
    provinceOptions,
    isLoadingProvinces: Boolean(isLoadingProvinces && isValidCountryId),
    isErrorProvinces: Boolean(isErrorProvinces && isValidCountryId),
    errorProvinces: isValidCountryId ? errorProvinces : null,
  }
}
